const STORAGE_KEY = 'ldCompareProducts';
const MAX_COMPARE = 4;
const COMPARE_PAGE = '/pages/compare';

function escapeHtml(value) {
  if (value == null) return '';
  const div = document.createElement('div');
  div.textContent = String(value);
  return div.innerHTML;
}

function readProducts() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, MAX_COMPARE) : [];
  } catch {
    return [];
  }
}

function writeProducts(products) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(products.slice(0, MAX_COMPARE)));
  document.dispatchEvent(new CustomEvent('ld:compare-change', { detail: { products } }));
}

function productFromToggle(input) {
  return {
    handle: input.dataset.compareHandle,
    title: input.dataset.compareTitle || input.dataset.compareHandle,
    url: input.dataset.compareUrl || `/products/${input.dataset.compareHandle}`,
    image: input.dataset.compareImage || '',
    price: input.dataset.comparePrice || '',
  };
}

function addProduct(product) {
  if (!product?.handle) return { ok: false, reason: 'missing' };
  const products = readProducts();
  if (products.some((item) => item.handle === product.handle)) {
    return { ok: true, products, already: true };
  }
  if (products.length >= MAX_COMPARE) {
    return { ok: false, reason: 'max', products };
  }
  products.push(product);
  writeProducts(products);
  return { ok: true, products };
}

function removeProduct(handle) {
  const products = readProducts().filter((item) => item.handle !== handle);
  writeProducts(products);
  return products;
}

function clearProducts() {
  writeProducts([]);
}

function isCompareButton(el) {
  return el?.tagName === 'BUTTON';
}

function setToggleActive(el, active, atMax) {
  if (isCompareButton(el)) {
    el.setAttribute('aria-pressed', active ? 'true' : 'false');
    el.classList.toggle('is-active', active);
    el.disabled = atMax;
    const label = el.querySelector('[data-compare-label]');
    if (label) label.textContent = active ? 'In compare' : label.dataset.compareIdleLabel || 'Compare';
    return;
  }

  el.checked = active;
  el.disabled = atMax;
}

function syncToggles(products = readProducts()) {
  const handles = new Set(products.map((item) => item.handle));
  document.querySelectorAll('[data-compare-toggle]').forEach((el) => {
    const handle = el.dataset.compareHandle;
    const active = handles.has(handle);
    setToggleActive(el, active, !active && products.length >= MAX_COMPARE);
  });
}

function renderBar(products = readProducts()) {
  const bar = document.querySelector('[data-compare-bar]');
  if (!bar) return;

  const itemsEl = bar.querySelector('[data-compare-bar-items]');
  const countEl = bar.querySelector('[data-compare-bar-count]');
  const cta = bar.querySelector('[data-compare-cta]');

  if (!products.length) {
    bar.hidden = true;
    document.body.classList.remove('ld-compare-bar-open');
    return;
  }

  bar.hidden = false;
  document.body.classList.add('ld-compare-bar-open');

  if (countEl) countEl.textContent = `${products.length} / ${MAX_COMPARE}`;

  if (itemsEl) {
    itemsEl.innerHTML = products
      .map(
        (product) => `
      <div class="ld-compare-bar__item">
        <a class="ld-compare-bar__thumb" href="${escapeHtml(product.url)}" title="${escapeHtml(product.title)}">
          ${
            product.image
              ? `<img src="${escapeHtml(product.image)}" alt="" width="56" height="56" loading="lazy">`
              : `<span class="ld-compare-bar__placeholder" aria-hidden="true"></span>`
          }
        </a>
        <button
          type="button"
          class="ld-compare-bar__remove"
          data-compare-remove="${escapeHtml(product.handle)}"
          aria-label="Remove ${escapeHtml(product.title)} from compare"
        >
          ×
        </button>
      </div>`
      )
      .join('');
  }

  if (cta) {
    const params = new URLSearchParams();
    params.set('products', products.map((item) => item.handle).filter(Boolean).join(','));
    cta.href = `${COMPARE_PAGE}?${params.toString()}`;
    const enabled = products.length >= 2;
    cta.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    cta.classList.toggle('is-disabled', !enabled);
  }
}

function extractJsonObject(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed) throw new Error('Empty compare-data response');
  try {
    return JSON.parse(trimmed);
  } catch {
    const start = trimmed.indexOf('{');
    const end = trimmed.lastIndexOf('}');
    if (start === -1 || end === -1 || end <= start) throw new Error('Invalid compare-data JSON');
    return JSON.parse(trimmed.slice(start, end + 1));
  }
}

function normalizeCompareData(raw, fallback = {}) {
  // Our alternate template returns a flat object. Asking for application/json
  // can make Shopify return { product: {...} } without metafields — unwrap that.
  const data = raw?.product && typeof raw.product === 'object' && !raw.handle ? raw.product : raw;
  const image =
    data.image ||
    data.featured_image ||
    (Array.isArray(data.images) ? data.images[0] : '') ||
    fallback.image ||
    '';

  const priceCents =
    data.price_cents != null
      ? Number(data.price_cents)
      : typeof data.price === 'number'
        ? data.price
        : fallback.price_cents != null
          ? Number(fallback.price_cents)
          : null;

  return {
    id: data.id ?? fallback.id,
    handle: data.handle || fallback.handle,
    title: data.title || fallback.title || data.handle || fallback.handle,
    url: data.url || fallback.url || `/products/${data.handle || fallback.handle}`,
    vendor: data.vendor || fallback.vendor || '',
    available: data.available ?? fallback.available ?? true,
    price_cents: Number.isFinite(priceCents) ? priceCents : null,
    price:
      (typeof data.price === 'string' && data.price.includes('£') ? data.price : null) ||
      data.price_label ||
      fallback.price ||
      '',
    image: typeof image === 'object' ? image.src || '' : image,
    compare_specs: Array.isArray(data.compare_specs) ? data.compare_specs : fallback.compare_specs || [],
    specifications:
      data.specifications && typeof data.specifications === 'object'
        ? data.specifications
        : fallback.specifications || {},
  };
}

async function fetchCompareData(handle, fallback = {}) {
  // Do not send Accept: application/json — Shopify then ignores ?view=compare-data
  // and returns the standard product JSON without our metafield specs.
  const response = await fetch(`/products/${encodeURIComponent(handle)}?view=compare-data`);
  if (!response.ok) throw new Error(`Failed to load ${handle}`);
  const raw = extractJsonObject(await response.text());
  return normalizeCompareData(raw, fallback);
}

function flattenSpecifications(specifications = {}) {
  const values = {};
  Object.entries(specifications || {}).forEach(([group, attrs]) => {
    if (!attrs || typeof attrs !== 'object') return;
    Object.entries(attrs).forEach(([label, value]) => {
      if (value == null || value === '') return;
      values[label] = Array.isArray(value) ? value.join(', ') : String(value);
    });
    // Keep group labels available for section headers when present as keys only.
    if (!values[`__group__:${group}`]) values[`__group__:${group}`] = group;
  });
  return values;
}

function buildRows(productsData) {
  const labelSet = new Set();
  const groupOrder = [];
  const groupForLabel = {};

  productsData.forEach((product) => {
    const specs = product.specifications || {};
    Object.keys(specs).forEach((group) => {
      if (!groupOrder.includes(group)) groupOrder.push(group);
      Object.keys(specs[group] || {}).forEach((label) => {
        labelSet.add(label);
        if (!groupForLabel[label]) groupForLabel[label] = group;
      });
    });

    (product.compare_specs || []).forEach((label) => {
      labelSet.add(label);
      if (!groupForLabel[label]) groupForLabel[label] = 'Specifications';
    });
  });

  // Prefer compare_specs order from the first product that defines them.
  const preferred = [];
  productsData.forEach((product) => {
    (product.compare_specs || []).forEach((label) => {
      if (labelSet.has(label) && !preferred.includes(label)) preferred.push(label);
    });
  });

  const remaining = [...labelSet].filter((label) => !preferred.includes(label));
  remaining.sort((a, b) => {
    const ga = groupForLabel[a] || '';
    const gb = groupForLabel[b] || '';
    if (ga !== gb) return groupOrder.indexOf(ga) - groupOrder.indexOf(gb) || ga.localeCompare(gb);
    return a.localeCompare(b);
  });

  const labels = [...preferred, ...remaining];
  const rows = [];
  let currentGroup = null;

  labels.forEach((label) => {
    const group = groupForLabel[label] || 'Specifications';
    if (group !== currentGroup) {
      rows.push({ type: 'group', label: group });
      currentGroup = group;
    }
    const values = productsData.map((product) => {
      const flat = flattenSpecifications(product.specifications);
      return flat[label] || '—';
    });
    const unique = new Set(values.map((value) => value.trim().toLowerCase()));
    rows.push({
      type: 'spec',
      label,
      values,
      same: unique.size <= 1,
    });
  });

  return rows;
}

function destinationVatRate() {
  const raw = document.documentElement.dataset.vatRate;
  if (raw == null || raw === '') return null;
  const rate = Number(raw);
  return Number.isFinite(rate) ? rate : null;
}

function formatIncVat(cents) {
  const rate = destinationVatRate();
  if (cents == null || rate == null) return '';
  const inc = Math.round((Number(cents) * (100 + rate)) / 100);
  const currency = document.documentElement.dataset.currency || window.Shopify?.currency?.active || 'GBP';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(inc / 100);
}

function renderComparePage(productsData, { diffOnly = false } = {}) {
  const emptyEl = document.querySelector('[data-compare-empty]');
  const wrapEl = document.querySelector('[data-compare-table-wrap]');
  const tableEl = document.querySelector('[data-compare-table]');
  if (!tableEl || !wrapEl || !emptyEl) return;

  if (!productsData.length) {
    emptyEl.hidden = false;
    wrapEl.hidden = true;
    tableEl.innerHTML = '';
    return;
  }

  emptyEl.hidden = true;
  wrapEl.hidden = false;

  const rows = buildRows(productsData).filter((row) => row.type === 'group' || !diffOnly || !row.same);
  const cleanedRows = [];
  rows.forEach((row, index) => {
    if (row.type === 'group') {
      const next = rows[index + 1];
      if (!next || next.type === 'group') return;
    }
    cleanedRows.push(row);
  });

  const head = `
    <thead>
      <tr>
        <th scope="col" class="ld-compare__sticky">Specification</th>
        ${productsData
          .map(
            (product) => `
          <th scope="col">
            <div class="ld-compare__product">
              <button
                type="button"
                class="ld-compare__remove"
                data-compare-remove="${escapeHtml(product.handle)}"
                aria-label="Remove ${escapeHtml(product.title)}"
              >Remove</button>
              <a class="ld-compare__media" href="${escapeHtml(product.url)}">
                ${
                  product.image
                    ? `<img src="${escapeHtml(product.image)}" alt="" width="120" height="120" loading="lazy">`
                    : ''
                }
              </a>
              <a class="ld-compare__name" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
              <p class="ld-compare__price">
                <span>${escapeHtml(product.price)} <span class="ld-vat-price__note">exc VAT</span></span>
                ${
                  formatIncVat(product.price_cents)
                    ? `<span class="ld-compare__price-inc">${escapeHtml(formatIncVat(product.price_cents))} inc VAT</span>`
                    : ''
                }
              </p>
              <a class="button button--secondary ld-compare__view" href="${escapeHtml(product.url)}">View product</a>
            </div>
          </th>`
          )
          .join('')}
      </tr>
    </thead>`;

  const body = `
    <tbody>
      ${cleanedRows
        .map((row) => {
          if (row.type === 'group') {
            return `<tr class="ld-compare__group"><th scope="row" colspan="${
              productsData.length + 1
            }">${escapeHtml(row.label)}</th></tr>`;
          }
          return `<tr class="ld-compare__row${row.same ? ' is-same' : ' is-diff'}">
            <th scope="row" class="ld-compare__sticky">${escapeHtml(row.label)}</th>
            ${row.values.map((value) => `<td>${escapeHtml(value)}</td>`).join('')}
          </tr>`;
        })
        .join('')}
    </tbody>`;

  tableEl.innerHTML = head + body;
}

async function initComparePage() {
  if (!document.querySelector('[data-compare-table]')) return;

  const params = new URLSearchParams(window.location.search);
  const fromUrl = (params.get('products') || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  let products = readProducts();

  if (fromUrl.length) {
    // Prefer URL order; keep known card metadata when present.
    const byHandle = new Map(products.map((item) => [item.handle, item]));
    products = fromUrl.map((handle) => byHandle.get(handle) || { handle, title: handle, url: `/products/${handle}` });
    writeProducts(products);
  }

  const diffInput = document.querySelector('[data-compare-diff]');
  let loaded = [];

  const refresh = async () => {
    const current = readProducts();
    if (!current.length) {
      loaded = [];
      renderComparePage([]);
      return;
    }
    const results = await Promise.allSettled(current.map((item) => fetchCompareData(item.handle, item)));
    loaded = results
      .map((result, index) => {
        if (result.status === 'fulfilled') return result.value;
        console.warn('[ld-compare] failed to load', current[index]?.handle, result.reason);
        return normalizeCompareData({}, current[index]);
      })
      .filter((item) => item?.handle);
    renderComparePage(loaded, { diffOnly: Boolean(diffInput?.checked) });
  };

  diffInput?.addEventListener('change', () => {
    renderComparePage(loaded, { diffOnly: Boolean(diffInput.checked) });
  });

  document.addEventListener('ld:compare-change', () => {
    refresh();
  });

  await refresh();
}

function bindUi() {
  document.addEventListener('change', (event) => {
    const input = event.target.closest('input[data-compare-toggle]');
    if (!input) return;

    if (input.checked) {
      const result = addProduct(productFromToggle(input));
      if (!result.ok && result.reason === 'max') {
        input.checked = false;
        window.alert(`You can compare up to ${MAX_COMPARE} products at a time.`);
      }
    } else {
      removeProduct(input.dataset.compareHandle);
    }
  });

  document.addEventListener(
    'click',
    (event) => {
      const buttonToggle = event.target.closest('button[data-compare-toggle]');
      if (buttonToggle) {
        event.preventDefault();
        event.stopPropagation();
        const active = buttonToggle.getAttribute('aria-pressed') === 'true';
        if (active) {
          removeProduct(buttonToggle.dataset.compareHandle);
        } else {
          const result = addProduct(productFromToggle(buttonToggle));
          if (!result.ok && result.reason === 'max') {
            window.alert(`You can compare up to ${MAX_COMPARE} products at a time.`);
          }
        }
        return;
      }

      const toggle = event.target.closest('.ld-compare-toggle');
      if (toggle) {
        // Dawn stretches the product title link over the whole card (z-index: 1).
        // Keep the compare control above it and stop the card navigation.
        event.stopPropagation();
        return;
      }

      const removeBtn = event.target.closest('[data-compare-remove]');
      if (removeBtn) {
        event.preventDefault();
        removeProduct(removeBtn.dataset.compareRemove);
        return;
      }

      const clearBtn = event.target.closest('[data-compare-clear]');
      if (clearBtn) {
        clearProducts();
        return;
      }

      const cta = event.target.closest('[data-compare-cta]');
      if (cta?.classList.contains('is-disabled')) {
        event.preventDefault();
      }
    },
    true
  );

  document.addEventListener('ld:compare-change', (event) => {
    const products = event.detail?.products || readProducts();
    syncToggles(products);
    renderBar(products);
  });

  const products = readProducts();
  syncToggles(products);
  renderBar(products);
}

bindUi();
initComparePage();
